import { AnyAction } from 'redux';
import { App, ThhPferdVorbelegungdaten, ValueLabelElement } from '@ps20-frontend/frontend-models';
import {
  CheckoutProdukt,
  GlobalActions,
  ProduktDeleted,
  ThhPferdProduktPayload,
  TiergruppeAnimal,
} from '@ps20-frontend/shop-integration-redux';
import { ThhTier, Tiergroesse } from '@ps20/bff-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { CheckoutActions } from '../checkout.actions';
import { ThhPferdAngabeChangedPayload } from '../action-types/thhPferdAngabeChangedPayload';
import { findEntryOfCard, tiergruppeEntryFieldsOf } from '../../utils/tiergruppe-animal.utils';

export interface ThhPferdAngaben {
  pferderassenListe: Array<ValueLabelElement>;
  angabenListe: Array<ThhPferdAngabenElement>;
  thhPferdAngebotVorhanden: boolean;
  tiere: ThhTier[];
}

export enum ThhPferdGeschlecht {
  M = 'M',
  W = 'W',
}

export enum PferdKennzeichnung {
  CHIP = 'Chip',
  LEBENSNUMMER = 'Lebensnummer',
  KEINE = 'Keine',
}

export interface ThhPferdAngabenElement {
  geschlecht: ThhPferdGeschlecht;
  name: string;
  geburtsdatum: string;
  rasse: string;
  chipnummer: string;
  lebensnummer: string;
  tiergroesse: Tiergroesse;
  kennzeichnung: PferdKennzeichnung;
  vorbelegt?: boolean;
  cardId?: string;
}

export function determineGeschlecht(
  isVorbelegt: boolean,
  tierGeschlecht: string,
  angabenGeschlecht: ThhPferdGeschlecht,
): ThhPferdGeschlecht {
  if (isVorbelegt) {
    return angabenGeschlecht;
  }

  if (['M', 'B'].includes(tierGeschlecht)) {
    return ThhPferdGeschlecht.M;
  }

  if (['W', 'A'].includes(tierGeschlecht)) {
    return ThhPferdGeschlecht.W;
  }

  return null;
}

export function determineKennzeichnung(
  isVorbelegt: boolean,
  buchnummer: string,
  identifizierungsart: string,
  kennzeichnung: PferdKennzeichnung,
): PferdKennzeichnung {
  if (isVorbelegt) {
    return kennzeichnung;
  }

  if (buchnummer && identifizierungsart !== 'C') {
    return PferdKennzeichnung.LEBENSNUMMER;
  } else if (identifizierungsart === 'C') {
    return PferdKennzeichnung.CHIP;
  } else {
    return PferdKennzeichnung.KEINE;
  }
}

const isThhHorseBreed = (
  breed: string | undefined,
  horseBreeds: ValueLabelElement[] | undefined,
): boolean => !!breed && (horseBreeds ?? []).some(horseBreed => horseBreed.value === breed);

const tiergruppeEntryOf = (
  entries: ThhPferdAngabenElement[],
  animal: TiergruppeAnimal | undefined,
  horseBreeds: ValueLabelElement[] | undefined,
): Partial<ThhPferdAngabenElement> => {
  if (!animal) {
    return {};
  }
  return {
    ...findEntryOfCard(entries, animal.cardId),
    ...tiergruppeEntryFieldsOf(animal, gender => determineGeschlecht(false, gender, null)),
    ...(isThhHorseBreed(animal.breed, horseBreeds) ? { rasse: animal.breed } : {}),
  };
};

const entryFor = (
  sectionState: ThhPferdAngaben,
  produkt: ThhPferdProduktPayload,
  index: number,
): Partial<ThhPferdAngabenElement> => {
  const tiergruppeAnimals = produkt.beibehalten ? undefined : produkt.tiergruppeAnimals;
  return tiergruppeAnimals
    ? tiergruppeEntryOf(
        sectionState.angabenListe,
        tiergruppeAnimals[index],
        sectionState.pferderassenListe,
      )
    : sectionState.angabenListe[index];
};

export const thhPferdAngabenReducer = function (
  sectionState: ThhPferdAngaben = null,
  action: AnyAction,
): ThhPferdAngaben {
  switch (action.type) {
    case GlobalActions.THH_PFERDERASSEN_LISTE_RETRIEVED: {
      const pferderassenListe: Array<ValueLabelElement> = action.payload;
      return { ...sectionState, pferderassenListe };
    }
    case GlobalActions.PRODUKT_ACTION: {
      const produkt: CheckoutProdukt = action.payload;
      if (produkt.appId !== App.THH_PFERD) {
        return sectionState;
      }
      const thhPferdProdukt: ThhPferdProduktPayload = action.payload;
      const emptyPferdAngabe: ThhPferdAngabenElement = {
        name: null,
        rasse: null,
        geschlecht: null,
        chipnummer: null,
        geburtsdatum: null,
        lebensnummer: null,
        tiergroesse: null,
        kennzeichnung: null,
      };

      let newAngabenListe: Array<ThhPferdAngabenElement> = [];

      if (
        !thhPferdProdukt.vorvertragsnummer ||
        thhPferdProdukt.isWeitererVertrag ||
        thhPferdProdukt.tiere?.length < 1 ||
        thhPferdProdukt.beibehalten
      ) {
        newAngabenListe = thhPferdProdukt.tiergroesseList.map(
          (ausgewaehlteTiergroessen, index) => ({
            ...emptyPferdAngabe,
            ...entryFor(sectionState, thhPferdProdukt, index),
            tiergroesse: ausgewaehlteTiergroessen,
          }),
        );
      } else {
        newAngabenListe = thhPferdProdukt.tiere?.map((tier, index) => {
          const angabenListe = sectionState.angabenListe[index];
          const isVorbelegt = angabenListe?.vorbelegt;

          return {
            ...emptyPferdAngabe,
            ...angabenListe,
            tiergroesse: thhPferdProdukt.tiergroesseList[index],
            rasse: updateIfIsNotVorbelegt(isVorbelegt, tier.tierrasse, angabenListe?.rasse),
            name: updateIfIsNotVorbelegt(isVorbelegt, tier.tiername, angabenListe?.name),
            geschlecht: determineGeschlecht(isVorbelegt, tier.geschlecht, angabenListe?.geschlecht),
            kennzeichnung: determineKennzeichnung(
              isVorbelegt,
              tier.buchnummer,
              tier.identifizierungsart,
              angabenListe?.kennzeichnung,
            ),
            lebensnummer: updateIfIsNotVorbelegt(
              isVorbelegt,
              tier.buchnummer ? tier.buchnummer : null,
              angabenListe?.lebensnummer,
            ),
            chipnummer: updateIfIsNotVorbelegt(
              isVorbelegt,
              tier.identifizierungsart === 'C' && tier.identifizierungscode
                ? tier.identifizierungscode
                : null,
              angabenListe?.chipnummer,
            ),
            geburtsdatum: updateIfIsNotVorbelegt(
              isVorbelegt,
              tier.geburtsdatumTier
                ? dayjs(tier.geburtsdatumTier).format(DATEFORMAT_LOCAL_STATE)
                : null,
              angabenListe?.geburtsdatum,
            ),
            vorbelegt: true,
          };
        });
      }

      return {
        ...sectionState,
        angabenListe: newAngabenListe,
        thhPferdAngebotVorhanden: !produkt.beibehalten,
      };
    }

    case GlobalActions.THH_PFERD_VORBELEGUNG_FINISHED: {
      const actionPayload: ThhPferdVorbelegungdaten = action.payload.vorhandeneVorbelegungdaten;
      return {
        ...sectionState,
        tiere: actionPayload?.tiere,
      };
    }

    case GlobalActions.PRODUKT_DELETED: {
      const produktDeletedPayload: ProduktDeleted = action.payload;
      if (
        produktDeletedPayload.angebotsIds.some(angebotsId => angebotsId.appId === App.THH_PFERD)
      ) {
        return {
          ...sectionState,
          angabenListe: [],
          thhPferdAngebotVorhanden: false,
        };
      }
      return sectionState;
    }

    case CheckoutActions.THH_PFERD_ANGABE_CHANGED: {
      const actionPayload: ThhPferdAngabeChangedPayload = action.payload;
      const newAngabenListe = sectionState.angabenListe.map((thhPferdAngabe, index) => {
        if (index === actionPayload.index) {
          return {
            ...thhPferdAngabe,
            ...actionPayload.changedValues,
          };
        } else {
          return thhPferdAngabe;
        }
      });
      return {
        ...sectionState,
        angabenListe: newAngabenListe,
      };
    }

    case CheckoutActions.THH_PFERD_KENNZEICHENART_CHANGED: {
      const actionPayload: AnyAction = action.payload;
      const newAngabenListe = sectionState.angabenListe.map((thhPferdAngabe, index) => {
        if (index === actionPayload.index) {
          return {
            ...thhPferdAngabe,
            kennzeichnung: actionPayload.kennzeichnung,
            chipnummer: sectionState.angabenListe[index].chipnummer
              ? sectionState.angabenListe[index].chipnummer
              : null,
            lebensnummer: sectionState.angabenListe[index].lebensnummer
              ? sectionState.angabenListe[index].lebensnummer
              : null,
          };
        } else {
          return thhPferdAngabe;
        }
      });
      return {
        ...sectionState,
        angabenListe: newAngabenListe,
      };
    }

    default:
      return sectionState;
  }
};

function updateIfIsNotVorbelegt(
  vorbelegt: boolean,
  element: string,
  sectionStateElement: string,
): string {
  return !vorbelegt ? element : sectionStateElement;
}
